import React from "react";
import PageMenu from "../components/PageMenu";
import { Col, Row } from "react-bootstrap";
import LBLPrescriberDetails from "../components/LBLPrescriberDetails";
import RadialBarsChart from "../../../../../../components/chart/RadialBarsChart";
import LBLDocContainer from "../components/LBLDocContainer";
import FullStackBarChart from "../../../../../../components/chart/FullStackBarChart";
import DistributedBarChart from "../../../../../../components/chart/DistributedBarChart";
import {
  transformNpsScoreData,
  transformPrescribersPercentageData,
  transformPrescribersSatisfactionScoreData,
} from "../../utils/commonFunctions";
import { useParams } from "react-router-dom";
import {
  useGetProjectDetails,
  useGetSummaryPrescribersHcpScore,
  useGetSummaryPrescribersLblDocument,
  useGetSummaryPrescribersLblSent,
  useGetSummaryPrescribersNpsScore,
} from "../../../../../../queries/query-hooks/workspace/emsl-hook";
import SquareChartSkeleton from "../../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import BarChartSkeleton from "../../../../../../skeleton/workspace/clirnet/brand-ads/bar-chart-skeleton";
import { ProjectHeaderTopBar, CardNotFound } from "@ui";

const ExpectedPrescribers = () => {
  const { project_id } = useParams();
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id, true);

  const contentIds = projectDetails?.content
    ?.map((item) => item?.content_id)
    .join(",");
  const payload = {
    start_date: projectDetails?.project_start_date,
    end_date: projectDetails?.project_end_date,
    content_id: contentIds,
  };

  const { data: npsScore, isLoading: isNpsScoreLoading } =
    useGetSummaryPrescribersNpsScore(project_id, payload);
  const { data: lblSent, isLoading: isLblSentLoading } =
    useGetSummaryPrescribersLblSent(project_id, payload);
  const { data: hcpScore, isLoading: isHcpScoreLoading } =
    useGetSummaryPrescribersHcpScore(project_id, payload);

  const { data: lblDocuments, isLoading: isLblDocumentsLoading } =
    useGetSummaryPrescribersLblDocument(project_id, payload);

  const formattedNpsScoreData = transformNpsScoreData(npsScore, ["NPS Scores"]);
  const formattedLblSentPercentageData =
    transformPrescribersPercentageData(lblSent);

  const formattedHCPScoreData = transformPrescribersSatisfactionScoreData(
    hcpScore,
    "HCP Satisfaction Score"
  );

  return (
    <div className="mb-3">
      <ProjectHeaderTopBar
        projectType="eMSL"
        title="Prescribers"
        projectStartDate={projectDetails?.project_start_date || ""}
        projectEndDate={projectDetails?.project_end_date || ""}
        status={projectDetails?.status}
        isProjectDetailsLoading={isProjectDetailsLoading}
      />
      <PageMenu name="prescribers" />

      <Row>
        {!isNpsScoreLoading &&
        !!formattedNpsScoreData.label &&
        !!formattedNpsScoreData.series ? (
          <Col md={12}>
            <FullStackBarChart
              title="Net Promoter Score (NPS)"
              tooltip="Measures HCP satisfaction and likelihood to recommend your brand, based on Detractors (0-6), Passives (7-8), and Promoters (9-10)"
              series={formattedNpsScoreData.series}
              labels={formattedNpsScoreData.label}
              graphColor={["#95A7D8", "#637EC8", "#3256BB"]}
            />
          </Col>
        ) : isNpsScoreLoading ? (
          <Col md={12}>
            <SquareChartSkeleton height={150} />
          </Col>
        ) : (
          <CardNotFound
            title={"Net Promoter Score (NPS)"}
            tooltip="Measures HCP satisfaction and likelihood to recommend your brand, based on Detractors (0-6), Passives (7-8), and Promoters (9-10)"
          />
        )}
      </Row>

      <Row>
        <Col md={12}>
          <LBLPrescriberDetails />
        </Col>
      </Row>

      <Row>
        <Col md={5} className="d-flex">
          {!isLblSentLoading &&
          !!formattedLblSentPercentageData.labels &&
          !!formattedLblSentPercentageData.series ? (
            <RadialBarsChart
              title={"LBL Sent Percentage"}
              tooltip="Printed or digital materials provided to HCP's post-interaction to reinforce key messages and support follow-up. 'Sent' indicates materials dispatched; 'Delivered' confirms receipt"
              labels={formattedLblSentPercentageData.labels}
              series={formattedLblSentPercentageData.series}
              graphColor={["#3256BB", "#95A7D8"]}
              showDownloadButtons={true}
              needCSVDownload={true}
            />
          ) : isLblSentLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound
              title={`LBL Sent Percentage`}
              isTitleCenter={true}
              height={360}
              tooltip="Printed or digital materials provided to HCP's post-interaction to reinforce key messages and support follow-up. 'Sent' indicates materials dispatched; 'Delivered' confirms receipt"
            />
          )}
        </Col>
        <Col md={7} className="d-flex">
          {!isHcpScoreLoading &&
          !!formattedHCPScoreData.labels &&
          !!formattedHCPScoreData.series ? (
            <DistributedBarChart
              title={"HCP Satisfaction Score"}
              tooltip="Measures the satisfaction level of Healthcare Professionals (HCPs) with your product or service, categorized as Poor, Unsatisfactory, Neutral, Satisfactory, and Very Satisfactory"
              labels={formattedHCPScoreData.labels}
              series={formattedHCPScoreData.series}
              exportCSVHeading="Satisfaction,"
              graphColor={[
                "#E5E9F2",
                "#C6CFE8",
                "#95A7D8",
                "#637EC8",
                "#3256BB",
              ]}
            />
          ) : isHcpScoreLoading ? (
            <BarChartSkeleton height={320} />
          ) : (
            <CardNotFound
              title={"HCP Satisfaction Score"}
              height={360}
              tooltip="Measures the satisfaction level of Healthcare Professionals (HCPs) with your product or service, categorized as Poor, Unsatisfactory, Neutral, Satisfactory, and Very Satisfactory"
            />
          )}
        </Col>
      </Row>

      <Row>
        {!isLblDocumentsLoading && lblDocuments?.length > 0 ? (
          <Col md={12}>
            <LBLDocContainer title="LBL Document" documents={lblDocuments} />
          </Col>
        ) : isLblDocumentsLoading ? (
          <BarChartSkeleton />
        ) : (
          <CardNotFound title={"LBL Document"} />
        )}
      </Row>
    </div>
  );
};

export default ExpectedPrescribers;
